import {
  useContestCorrectionReviewGet,
  useContestCorrectionReviewResolve,
} from "@sportsmet/contest-sdk/private";
import { Button, Checkbox, InlineBanner, Input, Select } from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import { countOf } from "../survivorGame/survivorGameCopy";
import { ReviewReasonField } from "./ReviewReasonField";

type Review = NonNullable<
  NonNullable<ReturnType<typeof useContestCorrectionReviewGet>["data"]>["data"]
>;
type EditedEntry = {
  entryId: string;
  status: "in" | "eliminated" | "revived";
  lives: string;
  points: string;
};
/** Admin-only parent mounts this; endpoint authorization is authoritative.
 * A snapshot is held while editing. A rejected token always discards it and
 * requires a fresh explicit review, never retries an old manual adjustment.
 * Every other API failure goes to the shared error modal; the inline banner
 * carries only the stale-review CONFLICT and neutral or success notices.
 */
export function ContestCorrectionReview({
  poolId,
  contestId,
}: {
  poolId: string;
  contestId: string;
}): ReactNode {
  const get = useContestCorrectionReviewGet(
    { poolId, contestId },
    { enabled: false },
  );
  const resolve = useContestCorrectionReviewResolve();
  const queryClient = useQueryClient();
  const [review, setReview] = useState<Review | null>(null);
  const [entries, setEntries] = useState<EditedEntry[]>([]);
  const [reason, setReason] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  async function load(): Promise<void> {
    setLoading(true);
    setReview(null);
    setAcknowledged(false);
    setReason("");
    setNotice(null);
    try {
      const result = await get.refetch();
      if (showApiError(result.data)) return;
      if (result.isError) {
        showOfflineError();
        return;
      }
      const next = result.data?.data ?? null;
      if (!next) {
        setNotice(
          "No pending correction requires review for this active contest.",
        );
        return;
      }
      setReview(next);
      setEntries(
        next.entries.map((entry) => ({
          entryId: entry.entryId,
          status: entry.status,
          lives: String(entry.livesRemaining),
          points: String(entry.pointsTotal),
        })),
      );
    } catch {
      showOfflineError();
    } finally {
      setLoading(false);
    }
  }
  const valid = entries.every((entry) => {
    const lives = Number(entry.lives),
      points = Number(entry.points);
    return (
      entry.lives.trim() !== "" &&
      entry.points.trim() !== "" &&
      Number.isInteger(lives) &&
      lives >= 0 &&
      lives <= 1000 &&
      Number.isInteger(points) &&
      Math.abs(points) <= 1000000 &&
      (entry.status === "eliminated") === (lives === 0)
    );
  });
  function change(index: number, patch: Partial<EditedEntry>): void {
    setEntries((current) =>
      current.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)),
    );
    setAcknowledged(false);
  }
  async function submit(decision: "reject" | "editAndApply"): Promise<void> {
    if (
      !review ||
      !reason.trim() ||
      !acknowledged ||
      resolve.isPending ||
      (decision === "editAndApply" && !valid) ||
      (decision === "reject" && review.gradingIncomplete)
    )
      return;
    setNotice(null);
    try {
      const result = await resolve.mutateAsync({
        poolId,
        contestId,
        roundId: review.roundId,
        resultsDigest: review.resultsDigest,
        reviewToken: review.reviewToken,
        decision,
        reason: reason.trim(),
        entries:
          decision === "reject"
            ? []
            : entries.map((entry) => ({
                entryId: entry.entryId,
                status: entry.status,
                livesRemaining: Number(entry.lives),
                pointsTotal: Number(entry.points),
              })),
      });
      if (extractErrorCode(result) === "CONFLICT") {
        setReview(null);
        setAcknowledged(false);
        await queryClient.invalidateQueries({
          queryKey: ["post:contest:contestCorrectionReviewGet"],
        });
        setNotice(
          "The review or totals changed, or the decision needs revision. Refresh the preview and review all current totals again. Nothing was automatically retried.",
        );
        return;
      }
      // The edits stay on screen so the commissioner can fix and resend.
      if (showApiError(result)) return;
      if (!result.data) {
        showOfflineError();
        return;
      }
      setReview(null);
      setAcknowledged(false);
      setNotice(
        decision === "reject"
          ? "Correction rejected. Current totals are unchanged."
          : "Correction resolved. The reviewed current totals are saved with your reason.",
      );
      await queryClient.invalidateQueries({
        predicate: (query) =>
          [
            "post:contest:standingsGet",
            "post:contest:resultsGetByMember",
            "post:contest:contestList",
          ].includes(String(query.queryKey[0])),
      });
    } catch {
      // The outcome is unknown, so the snapshot cannot be trusted any more.
      showOfflineError();
      setNotice("Refresh the preview before deciding again.");
      setReview(null);
      setAcknowledged(false);
    }
  }
  return (
    <section
      className="flex flex-col gap-4 rounded-lg border border-line p-4"
      aria-label="Score correction review"
    >
      <div>
        <h3 className="text-h3 text-ink">Score corrections</h3>
        <p className="text-sm text-ink-muted">
          Commissioner review for provider corrections after later play.
          Confirmed contests use their separate reopening process.
        </p>
      </div>
      <Button
        variant="secondary"
        loading={loading}
        disabled={resolve.isPending || loading}
        onClick={() => {
          void load();
        }}
      >
        {review ? "Refresh preview" : "Review score corrections"}
      </Button>
      {notice && <InlineBanner variant="neutral" message={notice} />}
      {review && (
        <>
          <InlineBanner
            variant="warning"
            message={
              review.gradingIncomplete
                ? "Grading is incomplete. Reconcile every entry's current totals before applying. This correction cannot be rejected."
                : "These are current totals, not an automatic replay of the corrected round. Set the totals that should stand after later play; historical picks and confirmed awards are preserved."
            }
          />
          <p className="text-sm text-ink-muted">
            Review all entries below. Zero lives requires Eliminated; active
            entries need at least one life. Points must be whole numbers.
          </p>
          {review.entries.map((original, index) => {
            const edited = entries[index];
            if (!edited) return null;
            return (
              <fieldset
                key={original.entryId}
                className="grid gap-3 rounded-md border border-line p-3 sm:grid-cols-3"
              >
                <legend className="px-1 font-semibold text-ink">
                  {original.label}
                </legend>
                <p className="text-sm text-ink-muted sm:col-span-3">
                  Current:{" "}
                  {original.status === "in"
                    ? "In"
                    : original.status === "revived"
                      ? "Revived"
                      : "Eliminated"}{" "}
                  · {countOf(original.livesRemaining, "life", "lives")} ·{" "}
                  {countOf(original.pointsTotal, "point", "points")}
                </p>
                <Select
                  label={`Status for ${original.label}`}
                  value={edited.status}
                  disabled={resolve.isPending}
                  items={[
                    { value: "in", label: "In" },
                    { value: "eliminated", label: "Eliminated" },
                    ...(original.status === "revived"
                      ? [{ value: "revived", label: "Revived" }]
                      : []),
                  ]}
                  onValueChange={(next) => {
                    change(index, {
                      // Safe: the items above are the only values offered.
                      status: next as EditedEntry["status"],
                    });
                  }}
                />
                <Input
                  label={`Lives for ${original.label}`}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={1000}
                  step={1}
                  value={edited.lives}
                  disabled={resolve.isPending}
                  onChange={(event) => {
                    change(index, { lives: event.target.value });
                  }}
                />
                <Input
                  label={`Points for ${original.label}`}
                  type="number"
                  min={-1000000}
                  max={1000000}
                  step={1}
                  value={edited.points}
                  disabled={resolve.isPending}
                  onChange={(event) => {
                    change(index, { points: event.target.value });
                  }}
                />
              </fieldset>
            );
          })}
          {!valid && (
            <InlineBanner
              variant="warning"
              message="Check lives, status and points. Every entry needs valid totals before applying."
            />
          )}
          <ReviewReasonField
            label="Reason for decision (required)"
            value={reason}
            disabled={resolve.isPending}
            onChange={(next) => {
              setReason(next);
              setAcknowledged(false);
            }}
          />
          <Checkbox
            label="I reviewed the current totals and my reason."
            description="Applying uses the values shown above; rejecting keeps the current totals."
            checked={acknowledged}
            disabled={resolve.isPending}
            onCheckedChange={(next) => {
              setAcknowledged(next === true);
            }}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={
                !valid || !reason.trim() || !acknowledged || resolve.isPending
              }
              loading={resolve.isPending}
              onClick={() => {
                void submit("editAndApply");
              }}
            >
              Apply reviewed totals
            </Button>
            <Button
              variant="secondary"
              disabled={
                review.gradingIncomplete ||
                !reason.trim() ||
                !acknowledged ||
                resolve.isPending
              }
              onClick={() => {
                void submit("reject");
              }}
            >
              Reject correction
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
